import type { MaterialRequirementBasis } from "~produce/helpers/materialRequirementBasisText";

import { GenericSelectData } from "~/components/select/SelectGeneric";
import { LocationDocument, MaterialItemDocument, MaterialVendorData } from "~/gql/graphqlBase.generated";
import { ymdToStartOfDayIso } from "~/helpers/timestampFormat";
import wsId from "~/helpers/wsId";
import { isoWeekOf, mondayOfIsoWeek, utcDateToYmd, ymdToUtcDate } from "~produce/inventory/hardgoods/helpers/readyWeek";
import { type PoCreateWizardDraft } from "~produce/purchasing/orders/create/helpers/poCreateWizard";
import { type DraftLineItem } from "~produce/purchasing/schedule/helpers/lineItemDiff";

export const GENERATE_PO_PARAM = "generate";

export interface GeneratePoSourceRow {
	available: number | null;
	eventDetailId: string;
	itemName: string;
	jobQuantity: number;
	materialItemId: string;
	multiple: number | null;
	productionJobId: string;
	requiredQuantity: number | null;
	siteLocationId: string | null;
	triggerDate: string | null;
	unitLabel: string | null;
}

export interface GeneratePoSpanHints {
	siteCount: number;
	weekCount: number;
}

interface GeneratePoShortfall {
	quantity: number;
	row: GeneratePoSourceRow;
}

const QUANTITY_EPSILON = 1e-9;

const compareLatestJobFirstUndatedLast = (left: GeneratePoSourceRow, right: GeneratePoSourceRow): number => {
	if (left.triggerDate === right.triggerDate) return 0;
	if (left.triggerDate === null) return 1;
	if (right.triggerDate === null) return -1;
	return left.triggerDate < right.triggerDate ? 1 : -1;
};

const computeItemSiteDeficit = (rows: readonly GeneratePoSourceRow[]): number =>
	Math.max(0, ...rows.map((row) => -(row.available ?? 0)));

const hasAvailableFigure = (row: GeneratePoSourceRow): boolean => row.available !== null && Boolean(row.siteLocationId);

export const findGeneratePoUnknownRows = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSourceRow[] =>
	sourceRows.filter((row) => !hasAvailableFigure(row));

export type GeneratePoNotice = "noShortfall" | "unknownShortfall";

export const selectGeneratePoNotices = (shortfallRowCount: number, unknownRowCount: number): GeneratePoNotice[] => {
	if (unknownRowCount > 0) return ["unknownShortfall"];
	return shortfallRowCount === 0 ? ["noShortfall"] : [];
};

export const computeGeneratePoShortfalls = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoShortfall[] => {
	const knownRows = sourceRows.filter(hasAvailableFigure);
	const rowsByItemAndSite = new Map<string, GeneratePoSourceRow[]>();
	for (const row of knownRows) {
		const key = `${row.materialItemId}|${row.siteLocationId}`;
		rowsByItemAndSite.set(key, [...(rowsByItemAndSite.get(key) ?? []), row]);
	}
	const shortfalls = new Map<GeneratePoSourceRow, number>();
	for (const rows of rowsByItemAndSite.values()) {
		let remaining = computeItemSiteDeficit(rows);
		for (const row of [...rows].sort(compareLatestJobFirstUndatedLast)) {
			if (remaining <= QUANTITY_EPSILON) break;
			const quantity = Math.min(Math.max(row.requiredQuantity ?? 0, 0), remaining);
			shortfalls.set(row, quantity);
			remaining -= quantity;
		}
	}
	return knownRows
		.map((row) => ({ quantity: shortfalls.get(row) ?? 0, row }))
		.filter((shortfall) => shortfall.quantity > QUANTITY_EPSILON);
};

export const findGeneratePoShortfallRows = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSourceRow[] =>
	computeGeneratePoShortfalls(sourceRows).map(({ row }) => row);

const defaultVendorFor = (
	material: Pick<MaterialItemDocument, "vendors"> | undefined
): MaterialVendorData | undefined => material?.vendors?.find((vendor) => vendor.isDefault);

export const resolveVendorFlag = (
	material: Pick<MaterialItemDocument, "vendors"> | undefined,
	poVendorId: string | null
): DraftLineItem["vendorFlag"] => {
	const defaultVendor = defaultVendorFor(material);
	if (!defaultVendor) return "noVendor";
	return defaultVendor.vendorId === poVendorId ? undefined : "vendorMismatch";
};

const findDominantVendorId = (
	rows: readonly GeneratePoSourceRow[],
	materialsById: ReadonlyMap<string, MaterialItemDocument>
): string | null => {
	const counts = new Map<string, number>();
	for (const row of rows) {
		const vendorId = defaultVendorFor(materialsById.get(row.materialItemId))?.vendorId;
		if (!vendorId) continue;
		counts.set(vendorId, (counts.get(vendorId) ?? 0) + 1);
	}
	let dominantVendorId: string | null = null;
	let dominantCount = 0;
	for (const [vendorId, count] of counts) {
		if (count > dominantCount) {
			dominantVendorId = vendorId;
			dominantCount = count;
		}
	}
	return dominantVendorId;
};

const findVendorName = (materials: readonly MaterialItemDocument[], vendorId: string): string | undefined =>
	materials.flatMap((material) => material.vendors ?? []).find((vendor) => vendor.vendorId === vendorId)?.vendorName;

const draftLineBasisOf = (row: GeneratePoSourceRow): MaterialRequirementBasis | undefined =>
	row.multiple === null || row.requiredQuantity === null
		? undefined
		: {
				containers: row.jobQuantity,
				multiple: row.multiple,
				required: row.requiredQuantity,
				unitLabel: row.unitLabel
			};

const buildDraftLine = (
	{ quantity, row }: GeneratePoShortfall,
	lineNumber: number,
	material: MaterialItemDocument | undefined,
	poVendorId: string | null
): DraftLineItem => {
	return {
		erpJobId: "",
		eventDetailId: row.eventDetailId,
		isCostOverridden: false,
		isDeleted: false,
		isDirty: false,
		isNew: true,
		itemName: material?.name ?? row.itemName,
		lineItemId: wsId(),
		lineNumber,
		materialItemErpId: material?.erpId ?? "",
		materialItemId: row.materialItemId,
		productionJobId: row.productionJobId,
		quantityOrdered: Math.ceil(quantity - QUANTITY_EPSILON),
		requiredBasis: draftLineBasisOf(row),
		unitCost: null,
		vendorFlag: resolveVendorFlag(material, poVendorId)
	};
};

const DATE_PART_LENGTH = 10;

const weekKeyOf = (row: GeneratePoSourceRow): string | null => {
	const triggerDay = ymdToUtcDate(row.triggerDate?.slice(0, DATE_PART_LENGTH) ?? "");
	if (!triggerDay) return null;
	const { week, year } = isoWeekOf(triggerDay);
	return utcDateToYmd(mondayOfIsoWeek(year, week));
};

const siteKeyOf = (row: GeneratePoSourceRow): string | null => row.siteLocationId || null;

const distinctKeys = (keys: readonly (string | null)[]): Set<string> =>
	new Set(keys.filter((key): key is string => key !== null));

const agreedKey = (keys: readonly (string | null)[]): string | null => {
	const distinct = distinctKeys(keys);
	const everyRowHasKey = keys.every((key) => key !== null);
	return everyRowHasKey && distinct.size === 1 ? [...distinct][0] : null;
};

const spanCount = (keys: readonly (string | null)[]): number => {
	const { size } = distinctKeys(keys);
	return size > 1 ? size : 0;
};

export const findAgreedGeneratePoSiteId = (sourceRows: readonly GeneratePoSourceRow[]): string | null =>
	agreedKey(findGeneratePoShortfallRows(sourceRows).map(siteKeyOf));

const findAgreedGeneratePoWeek = (sourceRows: readonly GeneratePoSourceRow[]): string | null =>
	agreedKey(findGeneratePoShortfallRows(sourceRows).map(weekKeyOf));

export const summarizeGeneratePoSpans = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSpanHints => {
	const shortfallRows = findGeneratePoShortfallRows(sourceRows);
	return { siteCount: spanCount(shortfallRows.map(siteKeyOf)), weekCount: spanCount(shortfallRows.map(weekKeyOf)) };
};

export const buildGeneratePoDraft = (
	sourceRows: readonly GeneratePoSourceRow[],
	allMaterialItems: readonly MaterialItemDocument[],
	agreedSite: LocationDocument | null = null
): PoCreateWizardDraft => {
	const materialsById = new Map(allMaterialItems.map((material) => [material.id, material]));
	const shortfalls = computeGeneratePoShortfalls(sourceRows);
	const shortfallRows = shortfalls.map(({ row }) => row);
	const vendorId = findDominantVendorId(shortfallRows, materialsById);
	const vendorName = vendorId ? findVendorName(allMaterialItems, vendorId) : undefined;
	const vendor: GenericSelectData | null = vendorId && vendorName ? { code: vendorId, name: vendorName } : null;

	const lines = shortfalls.map((shortfall, index) =>
		buildDraftLine(shortfall, index + 1, materialsById.get(shortfall.row.materialItemId), vendorId)
	);

	const agreedWeek = findAgreedGeneratePoWeek(sourceRows);
	const needByDate = agreedWeek ? ymdToStartOfDayIso(agreedWeek) : null;

	return { lines, needByDate, site: agreedSite, vendor };
};

export interface GeneratePoVendorDraft {
	draft: PoCreateWizardDraft;
	vendor: GenericSelectData;
}

export interface GeneratePoLeftOutItem {
	itemName: string;
	materialItemId: string;
}

export interface GeneratePoVendorPlan {
	leftOutItems: GeneratePoLeftOutItem[];
	vendorDrafts: GeneratePoVendorDraft[];
}

const compareVendorsByName = (left: GeneratePoVendorDraft, right: GeneratePoVendorDraft): number =>
	left.vendor.name.localeCompare(right.vendor.name) || left.vendor.code.localeCompare(right.vendor.code);

const groupShortfallsByDefaultVendor = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemDocument>
): {
	byVendor: Map<string, { shortfalls: GeneratePoShortfall[]; vendorName: string }>;
	withoutVendor: GeneratePoShortfall[];
} => {
	const byVendor = new Map<string, { shortfalls: GeneratePoShortfall[]; vendorName: string }>();
	const withoutVendor: GeneratePoShortfall[] = [];
	for (const shortfall of shortfalls) {
		const defaultVendor = defaultVendorFor(materialsById.get(shortfall.row.materialItemId));
		if (!defaultVendor) {
			withoutVendor.push(shortfall);
			continue;
		}
		const group = byVendor.get(defaultVendor.vendorId) ?? { shortfalls: [], vendorName: defaultVendor.vendorName };
		group.shortfalls.push(shortfall);
		byVendor.set(defaultVendor.vendorId, group);
	}
	return { byVendor, withoutVendor };
};

const distinctLeftOutItems = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemDocument>
): GeneratePoLeftOutItem[] => {
	const itemsById = new Map<string, GeneratePoLeftOutItem>();
	for (const { row } of shortfalls) {
		if (!itemsById.has(row.materialItemId))
			itemsById.set(row.materialItemId, {
				itemName: materialsById.get(row.materialItemId)?.name ?? row.itemName,
				materialItemId: row.materialItemId
			});
	}
	return [...itemsById.values()];
};

export const buildGeneratePoVendorPlan = (
	sourceRows: readonly GeneratePoSourceRow[],
	allMaterialItems: readonly MaterialItemDocument[],
	agreedSite: LocationDocument | null = null
): GeneratePoVendorPlan => {
	const materialsById = new Map(allMaterialItems.map((material) => [material.id, material]));
	const { byVendor, withoutVendor } = groupShortfallsByDefaultVendor(
		computeGeneratePoShortfalls(sourceRows),
		materialsById
	);
	const vendorDrafts = [...byVendor.entries()].map(([vendorId, group]): GeneratePoVendorDraft => {
		const agreedWeek = agreedKey(group.shortfalls.map(({ row }) => weekKeyOf(row)));
		return {
			draft: {
				lines: group.shortfalls.map((shortfall, index) =>
					buildDraftLine(shortfall, index + 1, materialsById.get(shortfall.row.materialItemId), vendorId)
				),
				needByDate: agreedWeek ? ymdToStartOfDayIso(agreedWeek) : null,
				site: agreedSite,
				vendor: { code: vendorId, name: group.vendorName }
			},
			vendor: { code: vendorId, name: group.vendorName }
		};
	});
	return {
		leftOutItems: distinctLeftOutItems(withoutVendor, materialsById),
		vendorDrafts: vendorDrafts.sort(compareVendorsByName)
	};
};

export default buildGeneratePoDraft;
